import { cva, type VariantProps } from 'class-variance-authority';
import { useState } from 'react';
import { cn } from './lib/cn';
import { initialsFor, TEAM_COLOR_CLASS, teamColorFor, type TeamColor } from './lib/palette';
import { PresenceDot, type PresenceDotProps } from './PresenceDot';
import type { PresenceState } from './lib/presence';

const avatar = cva('relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden font-semibold uppercase leading-none', {
  variants: {
    size: {
      xs: 'h-5 w-5 text-3xs',
      sm: 'h-6 w-6 text-2xs',
      md: 'h-8 w-8 text-xs', //  32px — sidebar team tiles, people rows
      lg: 'h-10 w-10 text-base', // 40px — post author
      xl: 'h-12 w-12 text-md',
      '2xl': 'h-14 w-14 text-lg',
      '3xl': 'h-20 w-20 text-2xl', // camera-off tile
      '4xl': 'h-24 w-24 text-3xl', // incoming-call popup
    },
    shape: {
      circle: 'rounded-pill',
      tile: 'rounded-md', // 8px — team/channel tiles in the sidebar
    },
  },
  defaultVariants: { size: 'md', shape: 'circle' },
});

const DOT_SIZE: Record<string, PresenceDotProps['size']> = {
  xs: 'xs', sm: 'xs', md: 'sm', lg: 'md', xl: 'md', '2xl': 'lg', '3xl': 'lg', '4xl': 'xl',
};

export interface AvatarProps extends VariantProps<typeof avatar> {
  /** Display name — drives initials fallback and the accessible label. */
  name: string;
  /** Photo URL. Falls back to initials on error or when omitted. */
  src?: string;
  /** Override the derived initials (e.g. channel "G", team "PD"). */
  initials?: string;
  /** Force a tile color. Omit for a deterministic color derived from `name`. */
  color?: TeamColor;
  /** Renders a presence dot at the bottom-right. */
  presence?: PresenceState;
  /**
   * Accessible name and tooltip for that dot. Defaults to the render state's
   * own label, which is not always the truth: these nine states are a collapse
   * of a richer vocabulary (Presenting and Screen sharing both land on `dnd`),
   * and they carry no sense of time, so an amber dot cannot say whether the
   * person stepped out a minute ago or on Friday. Pass the real sentence —
   * "Away · Last seen 15 minutes ago" — wherever the dot is the ONLY presence
   * affordance the surface has room for.
   */
  presenceLabel?: string;
  /** `false` when the caller shows `presenceLabel` in its own styled tooltip. */
  presenceNativeTooltip?: boolean;
  /** Ring color class for the presence dot — match the parent surface. */
  presenceRingClassName?: string;
  /** Outer ring, used by AvatarStack and the active-speaker treatment. */
  ring?: boolean;
  className?: string;
}

/** Person or team avatar with photo, initials fallback and optional presence. */
export function Avatar({
  name, src, initials, color, presence, presenceLabel, presenceNativeTooltip = true, presenceRingClassName,
  ring = false, size = 'md', shape = 'circle', className,
}: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(src) && !failed;
  const tone = color ?? teamColorFor(name);
  const label = initials ?? initialsFor(name);

  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      <span
        className={cn(
          avatar({ size, shape }),
          !showImage && TEAM_COLOR_CLASS[tone],
          showImage && 'bg-muted',
          ring && 'ring-2 ring-card',
        )}
        aria-hidden={Boolean(presence)}
      >
        {showImage ? (
          <img
            src={src}
            alt={name}
            loading="lazy"
            draggable={false}
            onError={() => setFailed(true)}
            className="h-full w-full object-cover drag-none"
          />
        ) : (
          <span aria-label={name}>{label}</span>
        )}
      </span>

      {presence ? (
        <PresenceDot
          state={presence}
          {...(presenceLabel === undefined ? {} : { label: presenceLabel })}
          nativeTooltip={presenceNativeTooltip}
          size={DOT_SIZE[size ?? 'md'] ?? 'sm'}
          ringClassName={presenceRingClassName}
          className="absolute -bottom-px -right-px"
        />
      ) : null}
    </span>
  );
}
